import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search } from 'lucide-react';
import { ImageUpload } from './ImageUpload';

// Preset gradient backgrounds
const GRADIENTS = [
  { id: 'blue', name: 'Blue Wave', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'sunset', name: 'Sunset', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'ocean', name: 'Ocean', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { id: 'forest', name: 'Forest', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
  { id: 'fire', name: 'Fire', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
  { id: 'purple', name: 'Purple Dream', gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
  { id: 'mint', name: 'Mint', gradient: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)' },
  { id: 'peach', name: 'Peach', gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
  { id: 'sky', name: 'Sky', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'lemon', name: 'Lemon', gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' },
  { id: 'rose', name: 'Rose', gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' },
  { id: 'aqua', name: 'Aqua', gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)' },
  { id: 'lavender', name: 'Lavender', gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)' },
  { id: 'emerald', name: 'Emerald', gradient: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)' },
  { id: 'coral', name: 'Coral', gradient: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)' },
  { id: 'midnight', name: 'Midnight', gradient: 'linear-gradient(135deg, #2e3192 0%, #1bffff 100%)' },
  { id: 'autumn', name: 'Autumn', gradient: 'linear-gradient(135deg, #f83600 0%, #f9d423 100%)' },
  { id: 'winter', name: 'Winter', gradient: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)' },
  { id: 'spring', name: 'Spring', gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
  { id: 'summer', name: 'Summer', gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)' },
];

// Solid colors
const COLORS = [
  { id: 'slate', name: 'Slate', color: '#64748b' },
  { id: 'gray', name: 'Gray', color: '#6b7280' },
  { id: 'red', name: 'Red', color: '#ef4444' },
  { id: 'orange', name: 'Orange', color: '#f97316' },
  { id: 'amber', name: 'Amber', color: '#f59e0b' },
  { id: 'yellow', name: 'Yellow', color: '#eab308' },
  { id: 'lime', name: 'Lime', color: '#84cc16' },
  { id: 'green', name: 'Green', color: '#22c55e' },
  { id: 'emerald', name: 'Emerald', color: '#10b981' },
  { id: 'teal', name: 'Teal', color: '#14b8a6' },
  { id: 'cyan', name: 'Cyan', color: '#06b6d4' },
  { id: 'sky', name: 'Sky', color: '#0ea5e9' },
  { id: 'blue', name: 'Blue', color: '#3b82f6' },
  { id: 'indigo', name: 'Indigo', color: '#6366f1' },
  { id: 'violet', name: 'Violet', color: '#8b5cf6' },
  { id: 'purple', name: 'Purple', color: '#a855f7' },
  { id: 'fuchsia', name: 'Fuchsia', color: '#d946ef' },
  { id: 'pink', name: 'Pink', color: '#ec4899' },
  { id: 'rose', name: 'Rose', color: '#f43f5e' },
];

export function BackgroundPicker({ open, onOpenChange, onSelectBackground, currentBackground, projectId, taskId }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredGradients = GRADIENTS.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredColors = COLORS.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectGradient = (gradient) => {
    onSelectBackground({
      type: 'gradient',
      value: gradient.gradient,
      id: gradient.id,
      name: gradient.name,
    });
    onOpenChange(false);
  };

  const handleSelectColor = (color) => {
    onSelectBackground({
      type: 'color',
      value: color.color,
      id: color.id,
      name: color.name,
    });
    onOpenChange(false);
  };

  const handleImageUpload = (imageUrl) => {
    onSelectBackground({
      type: 'image',
      value: imageUrl,
      id: `custom-${Date.now()}`,
      name: 'Custom Image',
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Choose a background</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search backgrounds..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="gradients" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="gradients">Gradients</TabsTrigger>
              <TabsTrigger value="colors">Solid Colors</TabsTrigger>
              <TabsTrigger value="upload">Upload</TabsTrigger>
            </TabsList>

            {/* Gradients Tab */}
            <TabsContent value="gradients" className="mt-4">
              <ScrollArea className="h-[400px] border rounded-md p-4">
                <div className="grid grid-cols-3 gap-3">
                  {filteredGradients.map((gradient) => (
                    <button
                      key={gradient.id}
                      onClick={() => handleSelectGradient(gradient)}
                      className={`group relative h-24 rounded-lg overflow-hidden transition-all hover:scale-105 ${
                        currentBackground?.id === gradient.id
                          ? 'ring-2 ring-primary ring-offset-2'
                          : ''
                      }`}
                      title={gradient.name}
                      style={{ background: gradient.gradient }}
                    >
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-2 backdrop-blur-sm">
                        {gradient.name}
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Colors Tab */}
            <TabsContent value="colors" className="mt-4">
              <ScrollArea className="h-[400px] border rounded-md p-4">
                <div className="grid grid-cols-5 gap-3">
                  {filteredColors.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => handleSelectColor(color)}
                      className={`group relative h-20 rounded-lg overflow-hidden transition-all hover:scale-105 ${
                        currentBackground?.id === color.id
                          ? 'ring-2 ring-primary ring-offset-2'
                          : ''
                      }`}
                      title={color.name}
                      style={{ backgroundColor: color.color }}
                    >
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] p-1 backdrop-blur-sm text-center">
                        {color.name}
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="mt-4">
              <div className="border rounded-md p-4 h-[400px] overflow-y-auto">
                <ImageUpload
                  currentImage={currentBackground?.type === 'image' ? currentBackground.value : null}
                  onImageSelected={handleImageUpload}
                  folderPath={`spaces/${projectId}/Tasks/${taskId}/backgrounds`}
                />
              </div>
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span>
              {filteredGradients.length} gradients • {filteredColors.length} colors
            </span>
            {currentBackground && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onSelectBackground(null);
                  onOpenChange(false);
                }}
              >
                Remove background
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
